<!--
  ClassStatsModal — 한 클래스의 바우처 사용 통계
  · GET /voucher/org/{org}/stats/classrooms/{classroom_idx}?date_from&date_to → 지정별 현황 · 월별 추이 · 늦게 합류한 학생
-->
<template>
  <a-modal :open="true" :title="title" :sub="subText" size="lg" @close="$emit('close')">
    <div v-if="loading" class="p-loader"><div class="p-spinner" /></div>
    <div v-else-if="error" class="p-alert p-alert--err"><div class="p-alert__body">{{ error }}</div></div>
    <template v-else-if="data">
      <div class="p-total-line">
        <span v-if="data.late_joiners">지정 후 합류 <strong>{{ data.late_joiners.count }}</strong>명 중 <strong>{{ data.late_joiners.registered }}</strong>명 등록</span>
        <span v-if="dateFrom || dateTo">등록일 {{ dateFrom || '…' }} ~ {{ dateTo || '…' }}</span>
      </div>

      <h4 class="p-sec">지정별 현황</h4>
      <a-empty v-if="!data.assignments || !data.assignments.length" title="지정이 없습니다" />
      <a-table v-else :columns="assignCols" :rows="data.assignments" row-key="classroom_voucher_idx" dense>
        <template #cell-title="{ row }">
          <div class="p-cell-title">{{ row.title }}</div>
          <div class="p-cell-sub p-mono">{{ row.barcode }}</div>
        </template>
        <template #cell-grade="{ value }"><a-pill kind="yellow">{{ gradeLabel(value) }}</a-pill></template>
        <template #cell-status="{ value }"><a-pill :kind="assignmentStatusKind(value)">{{ assignmentStatusLabel(value) }}</a-pill></template>
        <template #cell-register_until="{ value }"><span class="p-mono">{{ fmtDate(value) }}</span></template>
        <template #cell-registered="{ row }"><span class="p-mono">{{ row.registered_count }} / {{ row.unregistered_count }}</span></template>
        <template #cell-registered_amount="{ value }"><span class="p-amount">{{ fmtAmount(value, data.currency_code) }}</span></template>
        <template #cell-pending_estimate="{ value }"><span class="p-mono">{{ fmtAmount(value, data.currency_code) }}</span></template>
      </a-table>

      <h4 class="p-sec p-mt-16">월별 등록 추이</h4>
      <a-empty v-if="!data.monthly || !data.monthly.length" title="등록 내역이 없습니다" />
      <a-table v-else :columns="monthCols" :rows="data.monthly" row-key="month" dense>
        <template #cell-month="{ value }"><span class="p-mono">{{ value }}</span></template>
        <template #cell-registered_count="{ value }"><span class="p-mono">{{ value }}</span></template>
        <template #cell-registered_amount="{ value }"><span class="p-amount">{{ fmtAmount(value, data.currency_code) }}</span></template>
      </a-table>
    </template>
    <template #foot>
      <a-btn kind="ghost" @click="$emit('close')">닫기</a-btn>
    </template>
  </a-modal>
</template>

<script>
import AModal from '@/components/Pineapple/AModal.vue';
import ATable from '@/components/Pineapple/ATable.vue';
import APill from '@/components/Pineapple/APill.vue';
import ABtn from '@/components/Pineapple/ABtn.vue';
import AEmpty from '@/components/Pineapple/AEmpty.vue';
import { fmtAmount, fmtDate, gradeLabel, assignmentStatusKind, assignmentStatusLabel, classStatusLabel, voucherErrorText } from '../orgUtils';

export default {
  name: 'ClassStatsModal',
  components: { AModal, ATable, APill, ABtn, AEmpty },
  props: {
    orgIdx: { type: [Number, String], required: true },
    classroomIdx: { type: [Number, String], required: true },
    dateFrom: { type: String, default: '' },
    dateTo: { type: String, default: '' },
  },
  emits: ['close'],
  data() {
    return {
      data: null,
      loading: false,
      error: '',
      assignCols: [
        { key: 'title',             label: '도서' },
        { key: 'grade',             label: '등급',        width: '90px' },
        { key: 'status',            label: '상태',        width: '80px' },
        { key: 'register_until',    label: '기한',        width: '110px' },
        { key: 'registered',        label: '등록 / 미등록', width: '110px', align: 'right' },
        { key: 'registered_amount', label: '차감액',      width: '120px', align: 'right' },
        { key: 'pending_estimate',  label: '예상액',      width: '120px', align: 'right' },
      ],
      monthCols: [
        { key: 'month',             label: '월',     width: '120px' },
        { key: 'registered_count',  label: '등록 수', width: '100px', align: 'right' },
        { key: 'registered_amount', label: '차감액',  align: 'right' },
      ],
    };
  },
  computed: {
    title() {
      return this.data && this.data.classroom ? this.data.classroom.name : '클래스 사용 통계';
    },
    subText() {
      const c = this.data && this.data.classroom;
      if (!c || !c.start_date) return '';
      return `${fmtDate(c.start_date)} ~ ${fmtDate(c.end_date)} · ${classStatusLabel(c.class_status)}`;
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    fmtAmount, fmtDate, gradeLabel, assignmentStatusKind, assignmentStatusLabel,
    async load() {
      this.loading = true;
      this.error = '';
      try {
        const params = {};
        if (this.dateFrom) params.date_from = this.dateFrom;
        if (this.dateTo) params.date_to = this.dateTo;
        const res = await this.$axios({
          method: 'get',
          url: `/voucher/org/${this.orgIdx}/stats/classrooms/${this.classroomIdx}`,
          params,
          loadingBar: false,
        });
        this.data = res.data || null;
      } catch (e) {
        this.error = voucherErrorText(e, '클래스 통계를 불러오지 못했습니다.');
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../_org.scss';

.p-sec { font-size: 12px; font-weight: 800; color: $p-ink-mid; margin: 0 0 8px; letter-spacing: 0.02em; }
</style>
